import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import { mediaList } from '@/mocks/movies';

export default function FavoritesPage() {
  const { items, remove, clear } = useFavorites();
  const [confirmClear, setConfirmClear] = useState(false);

  usePageMeta({
    title: '我的收藏 - 菠萝蜜网',
    description: '查看您在菠萝蜜网收藏的影视作品，收藏记录保存在本浏览器本地，可随时移除。',
    keywords: '影视收藏,我的收藏,追剧清单,菠萝蜜网',
    path: '/favorites',
  });

  const hot = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 4);

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb items={[{ label: '我的收藏' }]} />

        <section className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-background-200 pb-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">我的收藏</h1>
            <p className="mt-1.5 text-xs text-foreground-500">
              共 {items.length} 部作品 · 收藏保存在当前浏览器本地，清除浏览器数据后会丢失
            </p>
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
              全部清空
            </button>
          )}
        </section>

        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-heart-3-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">还没有收藏任何影视作品</h2>
            <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
              在影视详情页点击“收藏”按钮，就能把喜欢的作品放进这里，方便下次回来继续看。
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Link to="/category/电影" className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600">
                去电影分类逛逛
              </Link>
              <Link to="/category/电视剧" className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-400">
                看看电视剧
              </Link>
            </div>
            <div className="mx-auto mt-8 max-w-xl text-left">
              <p className="mb-2 text-xs text-foreground-400">热门推荐，从这里开始</p>
              <div className="space-y-2">
                {hot.map((item) => (
                  <Link key={item.id} to={`/detail/${item.id}`} className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 p-2 transition hover:border-primary-300">
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                      <img src={item.poster} alt={item.title} title={item.title} className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground-900">{item.title}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-500">{item.type} · {item.year} · {item.genres.join(' / ')}</p>
                    </div>
                    <span className="text-xs font-medium text-accent-700">{item.rating.toFixed(1)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:items-center">
                <Link to={`/detail/${item.id}`} className="h-[112px] w-[80px] shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[124px] md:w-[88px]">
                  <img src={item.poster} alt={`${item.title} 海报`} title={`${item.title} 影视详情`} className="h-full w-full object-cover" />
                </Link>
                <div className="flex min-w-0 flex-1 flex-col">
                  <Link to={`/detail/${item.id}`} className="truncate font-heading text-sm font-bold text-foreground-950 transition hover:text-primary-700 md:text-base">
                    {item.title}
                  </Link>
                  <p className="mt-1 text-[11px] text-foreground-500">
                    {item.type} · {item.region} · {item.year} · {item.runtime}
                  </p>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1 rounded bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-900">
                      <i className="ri-star-fill w-3 h-3 flex items-center justify-center text-[10px]" />
                      {item.rating.toFixed(1)}
                    </span>
                    {item.genres.map((genre) => (
                      <span key={genre} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">{genre}</span>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-2 md:flex-row md:items-center">
                  <Link
                    to={`/detail/${item.id}`}
                    className="flex h-10 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 text-xs font-medium text-background-50 transition hover:bg-primary-600 md:h-9"
                  >
                    详情
                  </Link>
                  <button
                    type="button"
                    onClick={() => remove(item.id)}
                    className="flex h-10 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-xs text-foreground-700 transition hover:border-primary-400 hover:text-primary-700 md:h-9"
                  >
                    <i className="ri-heart-3-line w-3.5 h-3.5 flex items-center justify-center" />
                    移除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {confirmClear && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/50 px-4" onClick={() => setConfirmClear(false)}>
            <div className="w-full max-w-sm rounded-xl bg-background-50 p-5" onClick={(event) => event.stopPropagation()}>
              <h3 className="font-heading text-base font-bold text-foreground-950">清空全部收藏？</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">此操作会移除本地保存的全部收藏记录，且无法撤销。</p>
              <div className="mt-4 flex gap-2">
                <button type="button" onClick={() => setConfirmClear(false)} className="flex-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700">
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clear();
                    setConfirmClear(false);
                  }}
                  className="flex-1 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                >
                  确认清空
                </button>
              </div>
            </div>
          </div>
        )}
      </PortalShell>
    </SiteLayout>
  );
}